import { DIAGONAL_WORDS, validateTile } from './board-layout';
import type { CellCoord, CellData, CellOffset, TileData, ValidationResult } from './board-types';

export type LandingInput = {
  /** The dropped tile, anchored on the cell it was dropped onto. */
  tile: TileData;
  /** Which way the drop leaned inside that cell. Centred when left out, which tries right before left. */
  offset?: CellOffset;
  cells: CellData[];
  /** Every tile on the board, committed or not — the dropped tile itself may be among them. */
  boardTiles: TileData[];
  size?: number;
  validate?: (tile: TileData, board: TileData[]) => ValidationResult;
  diagonals?: boolean;
};

type Step = { dr: number; dc: number };

const RIGHT: Step = { dr: 0, dc: 1 };
const LEFT: Step = { dr: 0, dc: -1 };
const DOWN: Step = { dr: 1, dc: 0 };
const UP: Step = { dr: -1, dc: 0 };

const reversed = (step: Step): Step => ({ dr: -step.dr, dc: -step.dc });

/**
 * The four ways a tile slides off a cell that is taken: out along the axis it leaned furthest on, the
 * way it leaned, then back along that same axis, and only then the other one. A tile dropped on the
 * left of a letter therefore goes out to its left and one dropped on the right to its right.
 *
 * Both ways along the leaned axis come before either way across it because every tile of a turn has
 * to sit on one line: staying on the line the drop was aimed along keeps the move buildable, where
 * turning the corner at the first obstruction would break it. The corner is for a tile that can go
 * neither way along its own axis.
 */
function slideOrder(offset: CellOffset): Step[] {
  const across = offset.col >= 0 ? RIGHT : LEFT;
  const down = offset.row >= 0 ? DOWN : UP;
  const [first, second] = Math.abs(offset.col) >= Math.abs(offset.row) ? [across, down] : [down, across];
  return [first, reversed(first), second, reversed(second)];
}

/**
 * Where a dropped tile actually lands.
 *
 * A tile dropped onto a cell another tile already holds is not refused — it slides clear of whatever
 * is in the way and settles on the first cell that can take it, walking out along {@link slideOrder}
 * from the cell it was dropped on. Walking rather than stepping once is what puts it *beside the tile*
 * rather than inside it: the slide passes over every cell of the tile it landed on, and of anything
 * abutting that, and stops at the near edge of the run — so a tile dropped in the middle of a word
 * comes to rest at the end of it, on the side it was dropped from, and stays on the word's own line.
 * Holes are stepped over the same way; running off the board ends that direction and the next is tried.
 *
 * Returns the drop cell unchanged whenever nothing is in the way — **and equally when the drop fails
 * for any other reason**: a tile over a hole or off the board is not a tile obstructed by another one,
 * and no amount of sliding makes such a placement legal. Whether to allow those is the caller's own
 * ruling, which this deliberately leaves untouched. `null` means the opposite — a real collision with
 * nowhere on the board to put the tile, so the caller should keep it where it came from.
 */
export function resolveLanding({
  tile,
  offset = { row: 0, col: 0 },
  cells,
  boardTiles,
  size = 11,
  validate,
  diagonals = DIAGONAL_WORDS,
}: LandingInput): CellCoord | null {
  const at = (row: number, col: number): ValidationResult =>
    validateTile({ ...tile, row, col }, cells, boardTiles, size, validate, diagonals);

  const dropped: CellCoord = { row: tile.row, col: tile.col };
  if (at(dropped.row, dropped.col).reason !== 'collision') return dropped;

  for (const step of slideOrder(offset)) {
    for (let distance = 1; distance < size; distance++) {
      const row = dropped.row + step.dr * distance;
      const col = dropped.col + step.dc * distance;
      const result = at(row, col);
      if (result.valid) return { row, col };
      // The tile only ever moves further out along a direction it has already left the board on.
      if (result.reason === 'out-of-bounds') break;
    }
  }

  return null;
}
